import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useRealmAuthContext } from '@interxfi/api-client';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { Box } from '@interxfi/design-system/components/box/box';
import { Button } from '@interxfi/design-system/components/button';
import { LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container/wrapping/wrapping-layout-container';
import { TextButton } from '@interxfi/design-system/components/link/text-link/text-button';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list/readonly/item/readonly-list-item';
import { DEFAULT_READONLY_LIST_ITEM_INTERACTIVE_PADDING } from '@interxfi/design-system/components/list/readonly/item/readonly-list-item';
import { ReadonlyList } from '@interxfi/design-system/components/list/readonly/readonly-list';
import { Pill } from '@interxfi/design-system/components/pill/pill';
import type { CustomTableHeaderProps } from '@interxfi/design-system/components/table/custom-header';
import { Typography } from '@interxfi/design-system/components/typography';
import LockIcon from '@interxfi/design-system/icons/react/icon-lock';
import LogoutIcon from '@interxfi/design-system/icons/react/icon-logout';
import PhoneIcon from '@interxfi/design-system/icons/react/icon-phone';
import PinIcon from '@interxfi/design-system/icons/react/icon-pin';
import type { ResponsiveBoxSizing, SpaceUnit } from '@interxfi/design-system/util/responsive';
import { TableSectionHeader } from '../../../components/table/table-section-header';
import { GenerateRecoveryCodesDialog } from '../../../features/userauth/mfa/generate-recovery-codes-dialog';
import { MfaSetupDialog } from '../../../features/userauth/mfa/setup/mfa-setup-dialog';
import { UserauthSessionEndAllOthersConfirmDialog } from '../../../features/userauth/session/userauth-session-end-all-others-confirm-dialog';
import { UserauthSessionsTable } from '../../../features/userauth/session/userauth-sessions-table';
import { ChangeNameDialog } from '../../../features/userauth/user/settings/change-name-dialog';
import { ChangePasswordDialog } from '../../../features/userauth/user/settings/change-password-dialog';
import { ThemePreferenceRadioGroup } from '../../../features/userauth/user/settings/theme-preference-radio-group';
import { TimeZonePreferenceRadioGroup } from '../../../features/userauth/user/settings/time-zone-preference-radio-group';
import { getPageHead } from '../../../util/routing/meta';

const bottomElementOfLeftListPadding = '15px';

export const Route = createFileRoute('/_app/settings/personal')({
  loader: () =>
    ({
      breadcrumb: buildBreadcrumb({
        id: 'settings-personal',
        label: 'Settings',
        to: '/settings/personal',
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Settings'),
  component: PersonalSettings,
});

const rightBoxPadding = {
  top: { sm: '16px' },
  bottom: { sm: '32px' },
  left: { sm: '16px' },
  right: { sm: '16px' },
} as const satisfies ResponsiveBoxSizing<SpaceUnit>;

const wrappingRightBoxPadding = {
  top: { sm: '16px' },
  bottom: { sm: '16px' },
  left: { sm: '16px' },
  right: { sm: '16px' },
} as const satisfies ResponsiveBoxSizing<SpaceUnit>;

function SessionsTableHeader(props: CustomTableHeaderProps) {
  return (
    <TableSectionHeader
      actions={
        <UserauthSessionEndAllOthersConfirmDialog
          trigger={
            <Button affixIcon={<LogoutIcon />} size="small" variant="light">
              Sign Out All Other Sessions
            </Button>
          }
        />
      }
      {...props}
      title="Sessions"
    />
  );
}

function PersonalSettings() {
  const whoAmI = useRealmAuthContext();

  const leftListItems = useMemo(
    () =>
      [
        {
          id: 'name',
          title: 'Name',
          value: { string: { value: [whoAmI?.data?.profile?.firstName, whoAmI?.data?.profile?.lastName].filter(Boolean).join(' ') } },
          padding: DEFAULT_READONLY_LIST_ITEM_INTERACTIVE_PADDING,
          action: { type: 'modal', modal: { Component: ChangeNameDialog, props: { trigger: <TextButton size="small">Edit</TextButton> } } },
        },
        {
          id: 'email',
          title: 'Email',
          value: { string: { value: whoAmI?.email } },
        },
        {
          id: 'password',
          title: 'Password',
          value: { string: { value: '••••••••••••••••' } },
          padding: DEFAULT_READONLY_LIST_ITEM_INTERACTIVE_PADDING,
          action: { type: 'modal', modal: { Component: ChangePasswordDialog, props: { trigger: <TextButton size="small">Change</TextButton> } } },
        },
        {
          id: 'displayMode',
          title: 'Display Mode',
          padding: {
            bottom: '15px',
            top: '15px',
            left: '0px',
            right: '0px',
          },
          value: { node: { value: <ThemePreferenceRadioGroup /> } },
        },
        {
          id: 'timeZone',
          title: 'Time Zone',
          padding: {
            bottom: bottomElementOfLeftListPadding,
            top: '15px',
            left: '0px',
            right: '0px',
          },
          value: { node: { value: <TimeZonePreferenceRadioGroup /> } },
        },
      ] satisfies ReadonlyListItem[],
    [whoAmI?.data?.profile?.firstName, whoAmI?.data?.profile?.lastName, whoAmI?.email],
  );

  const rightListItems = useMemo(() => {
    const baseItems: ReadonlyListItem[] = [
      {
        id: 'authenticatorApp',
        icon: <PhoneIcon aria-hidden />,
        title: 'Authenticator App',
        padding: DEFAULT_READONLY_LIST_ITEM_INTERACTIVE_PADDING,
        pill: whoAmI?.hasMFA ? <Pill variant="green">Enabled</Pill> : <Pill variant="danger">Disabled</Pill>,
        action: {
          type: 'modal',
          modal: {
            Component: MfaSetupDialog,
            props: { trigger: <TextButton size="small">{whoAmI?.hasMFA ? 'Update' : 'Configure'}</TextButton> },
          },
        },
      },
    ];

    if (whoAmI?.hasMFA) {
      baseItems.push({
        id: 'backupCodes',
        icon: <PinIcon aria-hidden />,
        padding: DEFAULT_READONLY_LIST_ITEM_INTERACTIVE_PADDING,
        title: 'Backup Codes',
        action: {
          type: 'modal',
          modal: { Component: GenerateRecoveryCodesDialog, props: { trigger: <TextButton size="small">Generate New</TextButton> } },
        },
      });
    }

    return baseItems;
  }, [whoAmI?.hasMFA]);

  return (
    <PageLayout gap={DEFAULT_PAGE_LAYOUT_GAP} maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section>
        <WrappingLayoutContainer
          autoStickyRight={false}
          gap={DEFAULT_PAGE_LAYOUT_GAP}
          left={{
            ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
            render: <ReadonlyList items={leftListItems} />,
          }}
          padding={LAYOUT_ZERO_CONTAINER_PADDING}
          right={{
            ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
            render: (isWrapping) => (
              <Box
                backgroundColor="neutral-xlt"
                borderRadius="8px"
                padding={isWrapping ? wrappingRightBoxPadding : rightBoxPadding}
                style={isWrapping ? undefined : { height: `calc(100% - ${bottomElementOfLeftListPadding})` }}
              >
                <VerticalStack gap={4}>
                  <HorizontalStack blockAlign="center" gap={2} wrap="nowrap">
                    <LockIcon aria-hidden style={{ flexShrink: 0, minWidth: 16 }} width={16} />
                    <Typography.Level3>Two-Factor Authentication</Typography.Level3>
                  </HorizontalStack>

                  <ReadonlyList items={rightListItems} />
                </VerticalStack>
              </Box>
            ),
          }}
          wrap="forward"
          wrapGap={7}
        />
      </PageLayout.Section>

      <PageLayout.Section>
        <UserauthSessionsTable HeaderActions={SessionsTableHeader} userId={whoAmI?.userId} />
      </PageLayout.Section>
    </PageLayout>
  );
}
